<template>
    <div class="form-field-text-rich">
        <label :for="id" :class="labelClasses">{{ label }}</label>
        <div class="mt-2.5">
            <div :class="editorClasses" @click="editor?.chain().focus().run()">
                <div
                    class="flex flex-wrap items-center gap-1 px-3 py-2 border border-gray-300 bg-gray-50 rounded-t-md"
                    v-if="editor"
                >
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleBold().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('bold'),
                            'hover:bg-gray-100': !editor.isActive('bold'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Bold"
                    >
                        <IconTypeBold :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleItalic().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('italic'),
                            'hover:bg-gray-100': !editor.isActive('italic'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Italic"
                    >
                        <IconTypeItalic :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleUnderline().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('underline'),
                            'hover:bg-gray-100': !editor.isActive('underline'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Underline"
                    >
                        <IconTypeUnderline :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleStrike().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('strike'),
                            'hover:bg-gray-100': !editor.isActive('strike'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Strikethrough"
                    >
                        <IconTypeStrikethrough :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="toggleLink"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('link'),
                            'hover:bg-gray-100': !editor.isActive('link'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Link"
                    >
                        <IconLink :marginAuto="false" />
                    </button>
                    <button
                        v-if="enableButtons"
                        type="button"
                        @click="toggleButton"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('customButton'),
                            'hover:bg-gray-100': !editor.isActive('customButton'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Button"
                    >
                        <IconHandIndex :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="toggleImage"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-100"
                        title="Image"
                    >
                        <IconImage :marginAuto="false" />
                    </button>
                    <div class="w-px h-6 bg-gray-200 mx-1"></div>
                    <button
                        type="button"
                        @click="editor.chain().focus().setParagraph().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('paragraph'),
                            'hover:bg-gray-100': !editor.isActive('paragraph'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Paragraph"
                    >
                        <IconTextParagraph :marginAuto="false" />
                    </button>
                    <div class="w-px h-6 bg-gray-200 mx-1"></div>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleHeading({level: 2}).run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('heading', {level: 2}),
                            'hover:bg-gray-100': !editor.isActive('heading', {level: 2}),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Heading 2"
                    >
                        <IconTypeH2 :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleHeading({level: 3}).run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('heading', {level: 3}),
                            'hover:bg-gray-100': !editor.isActive('heading', {level: 3}),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Heading 3"
                    >
                        <IconTypeH3 :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleHeading({level: 4}).run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('heading', {level: 4}),
                            'hover:bg-gray-100': !editor.isActive('heading', {level: 4}),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Heading 4"
                    >
                        <IconTypeH4 :marginAuto="false" />
                    </button>
                    <div class="w-px h-6 bg-gray-200 mx-1"></div>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleBulletList().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('bulletList'),
                            'hover:bg-gray-100': !editor.isActive('bulletList'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Bullet List"
                    >
                        <IconListBullet :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleOrderedList().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('orderedList'),
                            'hover:bg-gray-100': !editor.isActive('orderedList'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Numbered List"
                    >
                        <IconListNumbered :marginAuto="false" />
                    </button>
                    <div class="w-px h-6 bg-gray-200 mx-1"></div>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleBlockquote().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('blockquote'),
                            'hover:bg-gray-100': !editor.isActive('blockquote'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Blockquote"
                    >
                        <IconQuote :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().toggleCodeBlock().run()"
                        :class="{
                            'bg-gray-200 text-black': editor.isActive('codeBlock'),
                            'hover:bg-gray-100': !editor.isActive('codeBlock'),
                        }"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out"
                        title="Code Block"
                    >
                        <IconCode :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().setHorizontalRule().run()"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-100"
                        title="Horizontal Rule"
                    >
                        <IconHr :marginAuto="false" />
                    </button>
                    <div class="w-px h-6 bg-gray-200 mx-1"></div>
                    <button
                        type="button"
                        @click="editor.chain().focus().undo().run()"
                        :disabled="!editor.can().chain().focus().undo().run()"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Undo"
                    >
                        <IconArrowCounterclockwise :marginAuto="false" />
                    </button>
                    <button
                        type="button"
                        @click="editor.chain().focus().redo().run()"
                        :disabled="!editor.can().chain().focus().redo().run()"
                        class="flex items-center justify-center w-8 h-8 rounded-md border-0 text-gray-700 cursor-pointer transition-all duration-150 ease-in-out hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Redo"
                    >
                        <IconArrowClockwise :marginAuto="false" />
                    </button>
                </div>
                <EditorContent :editor="editor" class="min-h-[150px] px-4 py-3" />
            </div>
        </div>

        <!-- Link Bubble Menu -->
        <BubbleMenu
            v-if="editor"
            :editor="editor"
            :tippyOptions="{duration: 100}"
            :shouldShow="
                ({editor, view, state, oldState, from, to}) => {
                    // Show when cursor is on a link
                    return editor.isActive('link');
                }
            "
            class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs"
        >
            <div class="text-sm text-gray-700 mb-3 break-all">
                <a
                    :href="editor?.getAttributes('link').href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-fuchsia-600 hover:text-fuchsia-800 underline font-medium"
                    @click.stop
                >
                    {{ editor?.getAttributes('link').href }}
                </a>
            </div>
            <div class="flex gap-2">
                <button
                    @click="toggleLink"
                    class="text-xs bg-fuchsia-50 text-fuchsia-700 px-3 py-1.5 rounded-sm border border-fuchsia-200 hover:bg-fuchsia-100 transition-colors font-medium"
                >
                    Edit Link
                </button>
                <button
                    @click="editor?.chain().focus().unsetLink().run()"
                    class="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-sm border border-red-200 hover:bg-red-100 transition-colors font-medium"
                >
                    Remove Link
                </button>
            </div>
        </BubbleMenu>

        <!-- Button Bubble Menu -->
        <BubbleMenu
            v-if="editor && enableButtons"
            :editor="editor"
            :tippyOptions="{duration: 100}"
            :shouldShow="
                ({editor, view, state, oldState, from, to}) => {
                    // Show when cursor is on a button
                    return editor.isActive('customButton');
                }
            "
            class="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs"
        >
            <div class="text-sm text-gray-700 mb-3 break-all">
                <a
                    :href="editor?.getAttributes('customButton').href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-fuchsia-600 hover:text-fuchsia-800 underline font-medium"
                    @click.stop
                >
                    {{ editor?.getAttributes('customButton').href }}
                </a>
            </div>
            <div class="flex gap-2">
                <button
                    @click="toggleButton"
                    class="text-xs bg-fuchsia-50 text-fuchsia-700 px-3 py-1.5 rounded-sm border border-fuchsia-200 hover:bg-fuchsia-100 transition-colors font-medium"
                >
                    Edit Button
                </button>
                <button
                    @click="editor?.chain().focus().extendMarkRange('customButton').unsetMark('customButton').run()"
                    class="text-xs bg-red-50 text-red-700 px-3 py-1.5 rounded-sm border border-red-200 hover:bg-red-100 transition-colors font-medium"
                >
                    Remove Button
                </button>
            </div>
        </BubbleMenu>
    </div>
</template>

<script setup>
import {useEditor, EditorContent, BubbleMenu} from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import CodeBlock from '@tiptap/extension-code-block';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Button from '~/components/button/Button.js';

const props = defineProps({
    id: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    field: {
        type: String,
        default: null,
    },
    value: {
        type: String,
        default: '',
    },
    placeholder: {
        type: String,
        default: 'Start typing...',
    },
    enableButtons: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update', 'focused']);

const editor = useEditor({
    content: props.value,
    extensions: [
        StarterKit.configure({
            heading: {
                levels: [2, 3, 4],
            },
            codeBlock: false, // Disable default codeBlock to use our own
        }),
        Placeholder.configure({
            placeholder: props.placeholder,
        }),
        Underline,
        CodeBlock,
        Link.configure({
            openOnClick: false,
        }),
        Image.configure({
            inline: false,
            allowBase64: false,
        }),
        ...(props.enableButtons ? [Button] : []),
    ],
    onUpdate: ({editor}) => {
        const html = editor.getHTML();
        emit('update', {field: props.field, value: html});
    },
    onFocus: ({editor}) => {
        const html = editor.getHTML();
        emit('focused', {field: props.field, value: html});
    },
});

watch(
    () => props.value,
    (newValue) => {
        const isSame = editor.value?.getHTML() === newValue;
        if (!isSame) {
            editor.value?.commands.setContent(newValue, false);
        }
    },
);

onBeforeUnmount(() => {
    editor.value?.destroy();
});

const toggleLink = () => {
    if (editor.value?.isActive('link')) {
        // Get current link URL
        const currentUrl = editor.value?.getAttributes('link').href || '';
        const url = prompt('Edit URL:', currentUrl);
        if (url !== null) {
            if (url === '') {
                editor.value?.chain().focus().unsetLink().run();
            } else {
                // Extend selection to mark if caret is inside link
                editor.value?.chain().focus().extendMarkRange('link').setLink({href: url}).run();
            }
        }
    } else {
        const url = prompt('Enter URL:');
        if (url) {
            editor.value?.chain().focus().setLink({href: url}).run();
        }
    }
};

const toggleButton = () => {
    if (editor.value?.isActive('customButton')) {
        // Get current button attributes
        const currentAttrs = editor.value?.getAttributes('customButton');
        const href = prompt('Edit button URL:', currentAttrs.href || '');
        if (href !== null) {
            if (href === '') {
                editor.value?.chain().focus().unsetMark('customButton').run();
            } else {
                // Extend selection to mark if caret is inside button
                editor.value?.chain().focus().extendMarkRange('customButton').setButton({href, class: 'v-btn'}).run();
            }
        }
    } else {
        const href = prompt('Enter button URL:');
        if (href) {
            editor.value?.chain().focus().setButton({href, class: 'v-btn'}).run();
        }
    }
};

const toggleImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
        editor.value?.chain().focus().setImage({src: url}).run();
    }
};

const labelClasses = computed(() => ({
    'text-md block font-medium leading-6 text-gray-900': true,
}));

const editorClasses = computed(() => ({
    'block w-full rounded-md border-0 shadow-xs': true,
    'text-gray-900': true,
    'ring-1 ring-inset ring-gray-300': true,
    'bg-white': true,
}));
</script>

<style>
@reference "../../assets/main.css";

.ProseMirror {
    @apply min-h-[150px] outline-none;
}
.ProseMirror p.is-editor-empty:first-child::before {
    @apply text-gray-400 float-left h-0 pointer-events-none;
    content: attr(data-placeholder);
}
.ProseMirror h2 {
    @apply text-2xl font-semibold mt-4 mb-2;
}
.ProseMirror h3 {
    @apply text-xl font-semibold mt-4 mb-2;
}
.ProseMirror h4 {
    @apply text-lg font-semibold mt-4 mb-2;
}
.ProseMirror p {
    @apply mb-3;
}
.ProseMirror ul,
.ProseMirror ol {
    @apply pl-6 mb-3;
}
.ProseMirror li {
    @apply mb-1;
}
.ProseMirror ul {
    @apply list-disc;
}
.ProseMirror ol {
    @apply list-decimal;
}
.ProseMirror u {
    @apply underline;
}
.ProseMirror s {
    @apply line-through;
}
.ProseMirror blockquote {
    @apply border-l-4 border-gray-300 pl-4 my-4 italic text-gray-700;
}
.ProseMirror pre {
    @apply bg-gray-100 rounded-md p-4 my-4 overflow-x-auto font-mono text-sm;
}
.ProseMirror hr {
    @apply border-0 border-t border-gray-300 my-6;
}
.ProseMirror a {
    @apply text-fuchsia-600 underline cursor-pointer;
}
.ProseMirror a:hover {
    @apply text-fuchsia-800;
}
.ProseMirror button.v-btn {
    @apply inline-block px-4 py-2 bg-fuchsia-600 text-white rounded-md hover:bg-fuchsia-700 cursor-pointer transition-colors duration-200 mr-2;
}
.ProseMirror img {
    @apply max-w-full h-auto rounded-md my-4 block;
}
.link-popover {
    min-width: 200px;
    max-width: 350px;
}
</style>
